import { formatDetectedAt } from './formatDetectedAt';
import { ABSENT_VALUE, formatRate } from './formatMeasuredValue';

export type EvidenceRow = {
  name: string;
  value: string;
};

// Each detector writes its own evidence keys and versions them through `schema`, so the
// key set is open: the page reads keys it has never seen rather than the one detector's shape.
const SCHEMA_KEY = 'schema';

// The detectors sum cache misses over every status code, so no part of the figure can be
// attributed to the requests an incident counts.
const CACHE_MISS_RENDERS_KEY = 'cacheMissRenders';

const HIDDEN_KEYS: readonly string[] = [SCHEMA_KEY, CACHE_MISS_RENDERS_KEY];

const RATE_KEY = /^rate\d*$|Rate$/;

const WORD_BOUNDARY = /([a-z0-9])([A-Z])/g;

const NUMBER_BOUNDARY = /([a-z])(\d)/g;

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

const readable = (key: string): string => {
  const spaced = key
    .replace(WORD_BOUNDARY, '$1 $2')
    .replace(NUMBER_BOUNDARY, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

const written = (key: string, value: unknown): string => {
  if (value === null || value === undefined) {
    return ABSENT_VALUE;
  }
  if (typeof value === 'number' && RATE_KEY.test(key)) {
    return formatRate(value * 100);
  }
  if (typeof value === 'string' && ISO_INSTANT.test(value)) {
    return formatDetectedAt(new Date(value));
  }

  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

export function evidenceRows(evidence: Record<string, unknown>): EvidenceRow[] {
  return Object.entries(evidence)
    .filter(([key]) => !HIDDEN_KEYS.includes(key))
    .map(([key, value]) => ({ name: readable(key), value: written(key, value) }));
}
